/*
* CM
* SD-WAN
* 터널설정
*
* @since 2022-11-11
* @author wj_tams_dev01
* */

import {CmApiPrefix} from '~/utils/restApi'
import { encryptionPassword } from '~/utils/cryptoHelper'

const urlPrefix = `${CmApiPrefix}/sdwan/tunnel-group`

export const state = () => ({
  tunnelGroupList: [],
  tunnelDeviceList: [],
  tunnelSearchList: [],
  tunnelLogList: [],
  tunnelPathLabelList: [],
  tunnelGroupPasswordInfo: {
    mode: 'ADD',
    encryptionAesKey: '',
    authPassword: '',
    authPasswordConfirm: ''
  },
  tunnelGroupInfo: {
    sdwan_tnl_grp_id: '',
    sdwan_tnl_grp_name: '',
    sdwan_tnl_grp_topology_type: 1,
    sdwan_tnl_grp_crypto_algr: 2,
    sdwan_tnl_grp_auth_algr: 2,
    sdwan_tnl_grp_auth_pw: '',
    sdwan_tnl_grp_interface_prefix: 111,
    sdwan_tnl_grp_desc: '',
    sdwan_tnl_grp_use_yn: 1,
    device_cnt: 0,
    path_label_cnt: 0,
    device_id_list: [],
    device_type_list: [],
    path_label_list: []
  },
  cryptoAlgrOptions: [{value: 1, label: 'AES256'}],
  authAlgrOptions: [{value: 1, label: 'SHA256'}],
  tunnelMaxLimitInfo: {
    maxGroupCount: 250,
    maxPathLabelCount: 16
  }
})

function tunnelSearchRoleConverter (role) {
  let roleName = ''

  if (role === 0) {
    roleName = 'Initiator'
  } else if (role === 1) {
    roleName = 'Responder'
  } else {
    roleName = 'Peer'
  }
  return roleName
}

export const mutations = {
  init (state) {
    state.tunnelGroupList = []
    state.tunnelDeviceList = []
    state.tunnelSearchList = []
    state.tunnelLogList = []
    state.tunnelPathLabelList = []
  },

  initTunnelGroupList (state, tunnelGroupList) {
    state.tunnelGroupList = tunnelGroupList
  },

  initTunnelDeviceList (state, tunnelDeviceList) {
    state.tunnelDeviceList = tunnelDeviceList
  },

  initTunnelSearchList (state, tunnelSearchList) {
    tunnelSearchList.forEach((searchItem) => {
      searchItem.local_role = tunnelSearchRoleConverter(searchItem.local_role)
      searchItem.remote_role = tunnelSearchRoleConverter(searchItem.remote_role)
    })
    state.tunnelSearchList = tunnelSearchList
  },

  initTunnelLogList (state, tunnelLogList) {
    state.tunnelLogList = tunnelLogList
  },

  initTunnelPathLabelList (state, tunnelPathLabelList) {
    state.tunnelPathLabelList = tunnelPathLabelList
  },

  initCryptoAlgorithm (state, cryptoAlgrList) {
    state.cryptoAlgrOptions = []
    if (!_.isArray(cryptoAlgrList)) {
      return
    }
    cryptoAlgrList.forEach((algrItem) => {
      state.cryptoAlgrOptions.push({
        value: algrItem.crypto_algr_id,
        label: algrItem.crypto_algr_name
      })
    })
  },
  initAuthAlgorithm (state, authAlgrList) {
    state.authAlgrOptions = []
    if (!_.isArray(authAlgrList)) {
      return
    }
    authAlgrList.forEach((algrItem) => {
      state.authAlgrOptions.push({
        value: algrItem.auth_algr_id,
        label: algrItem.auth_algr_name
      })
    })
  },
  updateForm: (state, payload) => {
    if (_.get(payload, 'setAll')) {
      state[payload.target] = payload.val
    } else {
      state[payload.target][payload.key] = payload.val
    }
  },
  clearForm (state, action) {
    state.tunnelGroupPasswordInfo = {
      mode: action,
      encryptionAesKey: '',
      authPassword: '',
      authPasswordConfirm: ''
    }
    // modified by wj_tams_dev01 (2023.03.27 설계 변경사항 적용) : 보안/인증 알고리즘 초기값 변경 (1 -> 2)
    let defaultValueCrypto = 1
    let defaultValueAuth = 1
    if (_.isArray(state.cryptoAlgrOptions) && state.cryptoAlgrOptions.length > 0 && _.isNumber(state.cryptoAlgrOptions[0].value)) {
      defaultValueCrypto = state.cryptoAlgrOptions[0].value
    }
    if (_.isArray(state.authAlgrOptions) && state.authAlgrOptions.length > 0 && _.isNumber(state.authAlgrOptions[0].value)) {
      defaultValueAuth = state.authAlgrOptions[0].value
    }
    state.tunnelGroupInfo = {
      sdwan_tnl_grp_id: '',
      sdwan_tnl_grp_name: '',
      sdwan_tnl_grp_topology_type: 1,
      sdwan_tnl_grp_crypto_algr: defaultValueCrypto,
      sdwan_tnl_grp_auth_algr: defaultValueAuth,
      sdwan_tnl_grp_auth_pw: '',
      sdwan_tnl_grp_interface_prefix: 111,
      sdwan_tnl_grp_desc: '',
      sdwan_tnl_grp_use_yn: 1,
      device_cnt: 0,
      path_label_cnt: 0,
      device_id_list: [],
      device_type_list: [],
      path_label_list: []
    }
    state.tunnelDeviceList = []
    // state.tunnelList = []
    state.tunnelPathLabelList = []
  },
  initMaxLimit (state, maxLimitInfo) {
    if (!_.isPlainObject(maxLimitInfo)) {
      return
    }
    if (_.has(maxLimitInfo, 'max_group_cnt') && _.isNumber(maxLimitInfo.max_group_cnt)) {
      state.tunnelMaxLimitInfo.maxGroupCount = maxLimitInfo.max_group_cnt
    }
    if (_.has(maxLimitInfo, 'max_path_label_cnt') && _.isNumber(maxLimitInfo.max_path_label_cnt)) {
      state.tunnelMaxLimitInfo.maxPathLabelCount = maxLimitInfo.max_path_label_cnt
    }
  }
}

export const actions = {

  /* 터널그룹 전체 조회 */
  async getTunnelGroupList ({commit}, {loading = true} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$get(`${urlPrefix}`, {loading})
      if (code === 'ok') {
        commit('initTunnelGroupList', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 - 장비 리스트 조회 */
  async getTunnelDeviceList ({commit, state}, {param, loading = true} = {}) {
    let res = {code: 'ok'}
    try {
      if (param.sdwan_tnl_grp_id < 0) {
        commit('initTunnelDeviceList', [])
        return res
      }
      const {code, result} = await this.$axios.$get(`${urlPrefix}/device/${param.sdwan_tnl_grp_id}`, {loading})

      if (code === 'ok') {
        commit('initTunnelDeviceList', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 - 경로 레이블 조회 */
  async getTunnelPathLabelList ({commit, state}, {param, loading = true} = {}) {
    let res = {code: 'ok'}
    try {
      if (param.sdwan_tnl_grp_id < 0) {
        commit('initTunnelPathLabelList', [])
        return res
      }

      const {code, result} = await this.$axios.$get(`${urlPrefix}/path-label/${param.sdwan_tnl_grp_id}`, {loading})
      if (code === 'ok') {
        commit('initTunnelPathLabelList', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 - 터널 조회 */
  async getTunnelSearchList ({commit, state}, {param, loading = true} = {}) {
    let res = {code: 'ok'}
    try {
      if (param.sdwan_tnl_grp_id < 0) {
        commit('initTunnelSearchList', [])
        return res
      }

      const {code, result} = await this.$axios.$post(`${urlPrefix}/tunnel`, param, {
        loading: loading,
        successAlert: false
      })
      if (code === 'ok') {
        commit('initTunnelSearchList', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 - 터널 생성 로그 */
  async getTunnelLogList ({commit, state}, {param, loading = true} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$post(`${urlPrefix}/tunnel/log`, param, {
        loading: loading,
        successAlert: false
      })
      if (code === 'ok') {
        commit('initTunnelLogList', result)
      } else {
        res = {code: 'nok', result: result}
      }
    } catch (e) {
      res = {code: 'nok', result: e}
    }
    return res
  },

  /* 터널 그룹 추가 */
  async addTunnelGroup ({commit, state}, {param, pwParam, limitErrorMsg, loading = true, successAlert = true} = {}) {
    let res = {code: 'ok'}
    try {
      // 터널 그룹 최대 등록 개수를 넘으면, 오류 처리
      if (state.tunnelGroupList.length >= state.tunnelMaxLimitInfo.maxGroupCount) {
        res = {code: 'nok', reason: limitErrorMsg}
        return res
      }
      // 패스워드 - Base64 인코딩 추가
      if (_.isString(pwParam.authPassword) && pwParam.authPassword.trim().length > 0) {
        param.sdwan_tnl_grp_auth_pw = encryptionPassword(pwParam.encryptionAesKey, pwParam.authPassword.trim())
      } else if (!_.isUndefined(param.sdwan_tnl_grp_auth_pw)) {
        param.sdwan_tnl_grp_auth_pw = null
      }
      const {code, result} = await this.$axios.$post(`${urlPrefix}`, param, {loading, successAlert})
      if (code === 'ok') {
        res.result = result
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널 그룹 수정(편집) */
  async updateTunnelGroup ({state}, {param, pwParam, loading = true, successAlert = true} = {}) {
    let res = {code: 'ok'}
    try {
      // 패스워드 - Base64 인코딩 추가
      if (_.isString(pwParam.authPassword) && pwParam.authPassword.trim().length > 0) {
        param.sdwan_tnl_grp_auth_pw = encryptionPassword(pwParam.encryptionAesKey, pwParam.authPassword.trim())
      } else if (!_.isUndefined(param.sdwan_tnl_grp_auth_pw)) {
        param.sdwan_tnl_grp_auth_pw = null
      }
      const {
        code, result
      } = await this.$axios.$put(`${urlPrefix}/${param.sdwan_tnl_grp_id}`, param, {loading, successAlert})
      if (code === 'ok') {
        res.result = result
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널 그룹 사용여부 */
  async useTunnelGroup ({state}, param) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$put(`${urlPrefix}/use`, param, {successAlert: false, loading: true})
      if (code !== 'ok') {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 삭제 */
  async deleteTunnelGroup ({state}, param) {
    let res = {code: 'ok'}
    try {
      const {
        code,
        result
      } = await this.$axios.$delete(`${urlPrefix}`, {
        data: param,
        successAlert: false,
        loading: false
      })
      if (code !== 'ok') {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 - 보안 알고리즘 조회 */
  async getCryptoAlgorithm ({commit, state}, {loading = false} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$get(`${urlPrefix}/crypto-algr`, {loading})
      if (code === 'ok') {
        commit('initCryptoAlgorithm', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 - 인증 알고리즘 조회 */
  async getAuthAlgorithm ({commit, state}, {loading = false} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$get(`${urlPrefix}/auth-algr`, {loading})
      if (code === 'ok') {
        commit('initAuthAlgorithm', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널그룹 - 그룹,경로레이블 최대 등록 가능 개수 조회 */
  async getMaxLimit ({commit, state}, {loading = false} = {}) {
    let res = {code: 'ok'}
    try {
      const {code, result} = await this.$axios.$get(`${urlPrefix}/limit`, {loading})
      if (code === 'ok') {
        commit('initMaxLimit', result)
      } else {
        res = {code: 'nok', reason: result.msg}
      }
    } catch (e) {
      res = {code: 'nok', reason: e}
    }
    return res
  },

  /* 터널 설정 적용/취소 */
  async finalCommand ({state}, type) {
    let res = {code: 'ok'}
    const apiUri = `${urlPrefix}/${type}`,
      axiosConfig = (type === 'apply') ? {applyMsg: true} : {cancelMsg: true}
    try {
      const result = await this.$axios.$put(apiUri, void 0, axiosConfig)
      res = result
    } catch (e) {
      res = {code: 'nok', result: {msg: e}}
    }
    return res
  }
}
